<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-exclamation-triangle"></i>
        {{ $t("fleetReports.problems_title") }}
      </h4>
    </div>

    <div class="box">
      <div class="columns">
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("fleetReports.from_date") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="fromDate"
                :clearable="false"
                @change="FetchReport"
            ></date-picker>
          </div>
        </div>
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("common.ຫາວັນທີ") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="toDate"
                :clearable="false"
                @change="FetchReport"
            ></date-picker>
          </div>
        </div>
        <div class="column is-4">
          <div class="control">
            <label class="label">{{ $t("fleetReports.route") }}</label>
            <model-list-select
                :list="boardRoutes"
                v-model="selectedRouteId"
                :placeholder="$t('common.ທັງໝົດ')"
                option-text="name"
                option-value="id"
                @input="FetchReport"
            >
            </model-list-select>
          </div>
        </div>
        <div class="column is-4">
          <div class="control field">
            <label class="label">&nbsp;</label>
            <div class="count-pill" :class="{zero: !count}">
              <i class="fal fa-flag"></i> {{ $t("fleetReports.problems_found", {n: count}) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="isLoadingReport"/>

    <p v-if="!isLoadingReport && !problems.length"
       :style="{textAlign: 'center', color: 'grey', padding: '25px'}">
      {{ $t("fleetReports.no_problems_in_range") }} 🎉
    </p>

    <div v-if="!isLoadingReport && problems.length" class="problem-list">
      <div v-for="p in problems" :key="p.id" class="problem-card">
        <div class="p-head">
          <span class="p-desc">{{ p.description }}</span>
          <span class="p-time">{{ FleetFormatDateTime(p.reported_at) }}</span>
        </div>

        <div class="p-meta">
          <span class="chip route">
            <i class="fal fa-route"></i> {{ p.route_name || '—' }}
            <template v-if="p.round_number"> · {{ $t("fleetReports.round_n", {n: p.round_number}) }}</template>
          </span>
          <router-link class="chip trip" :to="{ name: 'fleet.trips.detail', params: { id: p.trip_id } }">
            <i class="fal fa-truck"></i> {{ p.trip_code }}
          </router-link>
          <span v-if="p.direction" class="chip"
                :style="{color: FleetDirectionColor(p.direction), fontWeight: 700}">
            <i :class="FleetDirectionIcon(p.direction)"></i> {{ FleetDirectionLabel(p.direction) }}
          </span>
          <span class="chip" v-if="p.truck_plate">
            <i class="fal fa-id-card"></i> {{ p.truck_plate }}
          </span>
          <span class="chip status" :style="{background: StatusColor(p.status)}">
            {{ FleetTripStatusLabel(p.status) }}
          </span>
          <span class="chip" v-if="p.stop_name">
            <i class="fal fa-map-marker-alt"></i> {{ p.stop_name }}
          </span>
          <span class="chip" v-else>
            <i class="fal fa-map-marker-alt"></i> {{ $t("fleetReports.general_no_stop") }}
          </span>
          <span class="chip date">
            <i class="fal fa-calendar"></i> {{ FleetFormatDate(p.service_date) }}
          </span>
        </div>

        <div class="p-reporter">
          <i class="fal fa-user"></i>
          <span v-if="p.reporter_name">{{ p.reporter_name }}<template v-if="p.reporter_tel"> ({{ p.reporter_tel }})</template></span>
          <span v-else>{{ $t("fleetReports.reporter_user_id", {id: p.reporter_id}) }}</span>
        </div>

        <div class="p-images" v-if="p.images && p.images.length">
          <a v-for="(img, i) in p.images" :key="i" :href="img" target="_blank" rel="noopener">
            <img :src="img" class="p-thumb" :alt="$t('fleetReports.problem_image')">
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import {ModelListSelect} from 'vue-search-select';
import Loading from '@views/Loading';
import MixinFleetTrips from '@/Mixins/Fleet/Trips';

const STATUS_COLORS = {
  planned: '#e8a10f',
  in_progress: '#2dcc70',
  closed: '#195BFF',
  cancelled: '#e2513c',
};

/**
 * FR-023 ops triage: every driver-reported problem across trips in a date
 * window. Each card links to the trip detail. Self-hides gracefully on an old
 * backend (404 → empty state, no error spam).
 */
export default {
  name: 'FleetProblemsReport',
  components: {
    DatePicker,
    ModelListSelect,
    Loading,
  },
  mixins: [MixinFleetTrips],
  data: () => ({
    fromDate: moment(new Date()).startOf('month').format('YYYY-MM-DD'),
    toDate: moment(new Date()).format('YYYY-MM-DD'),
    selectedRouteId: '',
    problems: [],
    count: 0,
    isLoadingReport: false,
  }),
  methods: {
    StatusColor(status) {
      return STATUS_COLORS[status] || '#9aa0a6';
    },
    FetchReport() {
      this.isLoadingReport = true;
      this.FetchProblemsReport({
        from: this.fromDate,
        to: this.toDate,
        route_id: this.selectedRouteId || undefined,
      }).then((res) => {
        const data = (res.data && res.data.data) ? res.data.data : {};
        this.problems = data.problems || [];
        this.count = data.count || 0;
        this.isLoadingReport = false;
      }).catch((error) => {
        this.problems = [];
        this.count = 0;
        this.isLoadingReport = false;
        // Old backend (404) → quiet empty state; a real range error is surfaced.
        if (error && error.response && error.response.status !== 404) {
          this.$notification.error({message: this.FleetErrorText(error, this.$t('fleetReports.fetch_failed'))});
        }
      });
    },
  },
  created() {
    this.FetchBoardRoutes();
    this.FetchReport();
  },
};
</script>

<style scoped>
.count-pill {
  display: inline-block;
  background: #fff3e0;
  color: #e8a10f;
  border: 1px solid #f3d488;
  border-radius: 14px;
  padding: 6px 14px;
  font-weight: 700;
  font-size: 13px;
}

.count-pill.zero {
  background: #eef7ee;
  color: #2dcc70;
  border-color: #bfe3bf;
}

.count-pill i {
  margin-right: 4px;
}

.problem-list {
  margin-top: 6px;
}

.problem-card {
  border: 1px solid #f3d488;
  background: #fffdf6;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
}

.p-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.p-desc {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
}

.p-time {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.p-meta {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #5a6472;
  background: #f2f4f7;
  border-radius: 10px;
  padding: 3px 10px;
}

.chip.route {
  color: #195BFF;
  background: #eaf1ff;
}

.chip.trip {
  color: #195BFF;
  background: #eaf1ff;
  font-weight: 700;
  text-decoration: none;
}

.chip.trip:hover {
  background: #d9e5ff;
}

.chip.status {
  color: white;
}

.chip.date {
  margin-left: auto;
  color: #8a909a;
  background: transparent;
}

.p-reporter {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
}

.p-reporter i {
  margin-right: 4px;
}

.p-images {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.p-thumb {
  width: 90px;
  height: 90px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eee;
  cursor: pointer;
  transition: transform 0.1s ease;
}

.p-thumb:hover {
  transform: scale(1.03);
}
</style>
